import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftRight } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';

/**
 * In-app realm switcher. Renders ONLY for accounts carrying a cross-realm grant
 * (`user.realmAccess`, e.g. `['procurement','ims']`) — every normal single-realm
 * account has an empty grant, so this is invisible to them.
 *
 * It navigates between realm homes on the SAME origin, reusing the current
 * session token — no re-login. The backend realm gate makes the matching
 * exception for the granted realm, so the API calls on the other side succeed
 * with the same JWT.
 *
 * Today it toggles Purchase ⇄ IMS Containers (the only dual-realm grant in use);
 * the destination is whichever granted realm the user is NOT currently on.
 */
const REALM_HOME: Record<string, string> = {
  procurement: '/procurement',
  ims: '/ims/overview',
  porta: '/porta',
  spares: '/',
};
const REALM_LABEL: Record<string, string> = {
  procurement: 'Purchase',
  ims: 'IMS Containers',
  porta: 'Porta Cabin',
  spares: 'Spares',
};

export default function SwitchRealmButton({ variant = 'dark' }: { variant?: 'dark' | 'light' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const realms = Array.isArray(user?.realmAccess) ? user!.realmAccess! : [];
  // Fold the account's own primary realm into the switchable set so the button
  // can return to it even if realmAccess only lists the extra realm(s).
  const set = new Set<string>(realms);
  if (user?.procurementRole) set.add('procurement');
  if (user?.imsOnly) set.add('ims');
  if (user?.portaRole) set.add('porta');
  const switchable = Array.from(set).filter((r) => REALM_HOME[r]);
  if (switchable.length < 2) return null;

  const path = location.pathname;
  const current = path.startsWith('/ims') ? 'ims'
    : path.startsWith('/procurement') ? 'procurement'
    : path.startsWith('/porta') ? 'porta'
    : 'spares';
  // Destination = the next granted realm after the current one (simple toggle
  // for the common 2-realm case; cycles for >2).
  const others = switchable.filter((r) => r !== current);
  if (!others.length) return null;
  const target = others[0];

  return (
    <button
      type="button"
      onClick={() => navigate(REALM_HOME[target])}
      title={`Switch to ${REALM_LABEL[target]} — no re-login`}
      className={cn(
        'flex items-center gap-2 w-full px-3 py-2 rounded text-xs font-semibold transition-colors',
        variant === 'dark'
          ? 'text-amber-200 bg-amber-500/10 hover:bg-amber-500/20'
          : 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100',
      )}
    >
      <ArrowLeftRight size={14} /> Switch to {REALM_LABEL[target]}
    </button>
  );
}
